import { LitElement, html, unsafeCSS } from 'lit';
import { customElement, property, query } from 'lit/decorators.js';
import style from './accordion-item.scss?inline';

@customElement('lit-accordion-item')
export class LitAccordionItem extends LitElement {
    // Import styles
    static styles = [unsafeCSS(style)];
    static shadowRootOptions: ShadowRootInit = { mode: 'open' };

    @property({ type: String, reflect: true }) title = '';
    @property({ type: Boolean, reflect: true }) expanded = false;
    @property({ type: Number }) viewportWidth = window.innerWidth; // reaktiv für Resize

    @query('.accordion-item__panel') private panelEl!: HTMLDivElement;

    private static nextId = 0;
    private buttonId: string;
    private panelId: string;
    private resizeRaf?: number;

    constructor() {
        super();
        const uid = LitAccordionItem.nextId++;
        this.buttonId = `accordion-item__header-${uid}`;
        this.panelId = `accordion-item__panel-${uid}`;
    }

    connectedCallback() {
        super.connectedCallback();
        window.addEventListener('hashchange', this.onHashChange);
        window.addEventListener('resize', this.onResize); // neu
        // Check for anchor
        Promise.resolve().then(() => this.checkHashOpen());
    }

    disconnectedCallback() {
        window.removeEventListener('hashchange', this.onHashChange);
        window.removeEventListener('resize', this.onResize); // neu
        super.disconnectedCallback();
    }

    private onToggleClick = () => {
        const next = !this.expanded;
        this.dispatchEvent(new CustomEvent('accordion-item-toggle', {
            detail: { expanded: next },
            bubbles: true,
            composed: true,
        }));
        this.expanded = next;
    };

    private onHeaderKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            this.onToggleClick();
        }
    };

    private setPanelMaxHeight() {
        if (!this.panelEl) return;
        const h = this.panelEl.scrollHeight;
        this.style.setProperty('--accordion-item-panel-max', `${h}px`);
    }

    private onResize = () => {
        if (this.resizeRaf) cancelAnimationFrame(this.resizeRaf);
        this.resizeRaf = requestAnimationFrame(() => {
            this.viewportWidth = window.innerWidth;
        });
    };

    protected async updated(changed: Map<string, unknown>) {
        if (changed.has('expanded')) {
            // Measure height and set as variable when opening
            if (this.expanded) {
                await this.updateComplete;
                this.setPanelMaxHeight();
            }
        }

        // Viewport changed -> re-measure if expanded
        if (changed.has('viewportWidth') && this.expanded) {
            await this.updateComplete;
            this.setPanelMaxHeight();
        }
    }

    private onSlotChange = () => {
        // Content has changed – update height if open
        if (this.expanded) this.setPanelMaxHeight();
    }

    open() {
        if (!this.expanded) this.onToggleClick();
    }
    close() {
        if (this.expanded) this.onToggleClick();
    }

    private onHashChange = () => {
        this.checkHashOpen();
    };

    private getAnchorTarget(): Element | null {
        const raw = window.location.hash;
        if (!raw || raw.length < 2) return null;
        const id = decodeURIComponent(raw.slice(1));
        // primarily look for id, fallback to named anchor
        return document.getElementById(id) || document.querySelector(`a[name="${id}"]`);
    }

    private async checkHashOpen() {
        const target = this.getAnchorTarget();
        if (!target) return;

        // Is the target inside this Accordion-Item?
        const hostItem = target.closest('lit-accordion-item');
        if (hostItem !== this) return;

        if (!this.expanded) {
            this.open();
            // After opening, ensure scrolling to the anchor
            await this.updateComplete;
            // small delay to allow the transition to start
            setTimeout(() => {
                target.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }, 0);
        }
    }

    render() {
        const block = 'accordion-item';
        const classes = [
            block,
            this.expanded ? `${block}--expanded` : '',
        ].filter(Boolean).join(' ');

        return html`
            <div class=${classes} part="item">
                <button
                    id=${this.buttonId}
                    class="${block}__header"
                    part="header button"
                    @click=${this.onToggleClick}
                    @keydown=${this.onHeaderKeyDown}
                    aria-controls=${this.panelId}
                    aria-expanded=${String(this.expanded)}
                >
                    <slot name="header">${this.title}</slot>
                    <span class="${block}__icon" part="icon" aria-hidden="true">
                        <?xml version="1.0" encoding="utf-8"?>
                        <svg version="1.1" id="Ebene_1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" x="0px" y="0px"
                            width="25px" height="15px" viewBox="0 0 25 15" enable-background="new 0 0 25 15" xml:space="preserve">
                            <polygon points="0,2.48 2.47,0 12.5,10.05 22.53,0 25,2.48 12.5,15 "/>
                        </svg>
                    </span>
                </button>

                <div
                    id=${this.panelId}
                    class="${block}__panel"
                    part="content"
                    role="region"
                    aria-labelledby=${this.buttonId}
                    ?hidden=${!this.expanded}
                >
                    <slot name="content" @slotchange=${this.onSlotChange}></slot>
                </div>
            </div>
        `;
    }
}
